import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCreateInternalChannel,
  useUpdateInternalChannel,
} from "@/components/pages/chats/hooks/useChannel";
import { toast } from "sonner";
import {
  CreateGroupChatFormData,
  createGroupChatSchema,
} from "../../utils/chatSchemas";
import { useTranslation } from "react-i18next";
import { useUsers } from "@/components/pages/users/hooks/useUsers";
import { Checkbox } from "@/components/ui/checkbox";
import ChannelUserAvatar from "./ChannelUserAvatar";
import { useInternalChatClient } from "@/components/pages/chats/hooks/useInternalChatClient";
import { Role } from "@/types/user";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { X } from "lucide-react";

interface CreateGroupChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: {
    id: string;
    name: string;
    targetUserIds: string[];
  };
}

export const CreateGroupChatModal: React.FC<CreateGroupChatModalProps> = ({
  isOpen,
  onClose,
  initialData,
}) => {
  const { t } = useTranslation();
  const { capabilities } = useAccessCapabilities();
  const isInternalChatEnabled = capabilities.canUseInternalChat;
  const { userId: currentUserId } = useInternalChatClient(
    isOpen && isInternalChatEnabled,
  );
  const { data: users, isLoading: isLoadingUsers } = useUsers(
    {
      roles: [Role.STAFF, Role.FRONT_DESK, Role.HOTEL_ADMIN],
    },
    isOpen && isInternalChatEnabled,
  );

  const { mutate: createChannel, isPending: isCreating } =
    useCreateInternalChannel();
  const { mutate: updateChannel, isPending: isUpdating } =
    useUpdateInternalChannel();

  const isEditing = !!initialData;

  const isProcessingRef = useRef(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateGroupChatFormData>({
    resolver: zodResolver(createGroupChatSchema),
    defaultValues: {
      name: "",
      targetUserIds: [],
    },
  });

  const selectedUserIds = watch("targetUserIds") || [];

  useEffect(() => {
    if (isOpen) {
      isProcessingRef.current = false;

      if (initialData) {
        reset({
          name: initialData.name,
          targetUserIds: initialData.targetUserIds.filter(
            (id) => id !== currentUserId,
          ),
        });
      } else {
        reset({
          name: "",
          targetUserIds: [],
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onSubmit = (data: CreateGroupChatFormData) => {
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;

    if (isEditing && initialData) {
      updateChannel(
        { id: initialData.id, dto: data },
        {
          onSuccess: () => {
            toast.success(t("chats.successUpdate"));
            onClose();
          },
          onError: (error) => {
            toast.error(t("chats.errorUpdate"));
            console.error("Failed to update channel:", error);
            isProcessingRef.current = false;
          },
        },
      );
    } else {
      createChannel(
        { ...data, chat_type: "group" },
        {
          onSuccess: () => {
            toast.success(t("chats.successCreate"));
            onClose();
          },
          onError: (error) => {
            toast.error(t("chats.errorCreate"));
            console.error("Failed to create channel:", error);
            isProcessingRef.current = false;
          },
        },
      );
    }
  };

  const toggleUser = (userId: string) => {
    const currentSelected = [...selectedUserIds];
    const index = currentSelected.indexOf(userId);
    if (index > -1) {
      currentSelected.splice(index, 1);
    } else {
      currentSelected.push(userId);
    }
    setValue("targetUserIds", currentSelected);
  };

  const filteredUsers =
    users?.filter((user) => user.id !== currentUserId) || [];

  const isLoadingOrEmpty = isLoadingUsers || filteredUsers.length === 0;

  const getSubmitButtonLabel = () => {
    if (isCreating || isUpdating) {
      return t(isEditing ? "chats.updating" : "chats.creating");
    }
    return t(isEditing ? "chats.update" : "chats.create");
  };

  if (!isInternalChatEnabled) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogOverlay />
      <DialogContent
        showCloseButton={false}
        className="border-base-border flex max-h-[min(720px,calc(100vh-48px))] w-[520px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-xl border bg-white p-0 shadow-lg sm:max-w-[520px]"
      >
        <DialogHeader className="border-base-border relative shrink-0 gap-1 border-b px-6 py-4 pr-14 text-left">
          <DialogTitle className="text-base leading-normal font-semibold text-black/96">
            {isEditing ? t("chats.editGroupChat") : t("chats.createGroupChat")}
          </DialogTitle>
          <DialogDescription className="text-sm leading-normal text-black/65">
            {t("chats.groupChatDescription")}
          </DialogDescription>
          <DialogClose asChild>
            <Button
              type="button"
              variant="new_ghost"
              size="icon_sm"
              className="absolute top-3 right-4 rounded-md text-black/55"
              disabled={isCreating || isUpdating}
              aria-label={t("chats.closeDialog")}
            >
              <X />
            </Button>
          </DialogClose>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
            <div className="flex flex-col gap-1.5">
              <Input
                id="group-chat-name"
                label={t("chats.chatName")}
                placeholder={t("chats.enterChatName")}
                aria-invalid={Boolean(errors.name) || undefined}
                autoComplete="off"
                {...register("name")}
              />
              {errors.name ? (
                <p className="text-custom-destructive-dark text-sm">
                  {t("chats.chatNameRequired")}
                </p>
              ) : null}
            </div>

            <div className="flex min-h-0 flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-black/96">{t("chats.members")}</Label>
                <span className="text-xs text-black/55">
                  {t("chats.selectedCount", {
                    count: selectedUserIds.length,
                  })}
                </span>
              </div>

              <div className="border-base-border min-h-[250px] overflow-hidden rounded-md border bg-white">
                <div className="max-h-[320px] overflow-y-auto">
                  {isLoadingUsers ? (
                    <div className="p-4 text-center text-sm text-black/55">
                      {t("chats.loading")}
                    </div>
                  ) : null}

                  {!isLoadingUsers && filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-sm text-black/55">
                      {t("users.table.noUsersFound")}
                    </div>
                  ) : null}

                  {!isLoadingOrEmpty ? (
                    <div className="divide-base-border divide-y">
                      {filteredUsers.map((user) => {
                        const checkboxId = `group-chat-user-${user.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

                        return (
                          <label
                            key={user.id}
                            htmlFor={checkboxId}
                            className="hover:bg-secondary-badge flex cursor-pointer items-center justify-between gap-3 p-3 transition-colors"
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <ChannelUserAvatar
                                name={`${user.firstName} ${user.lastName}`}
                              />
                              <span className="flex min-w-0 flex-col gap-1">
                                <span className="truncate text-sm font-medium text-black/96">
                                  {user.firstName} {user.lastName}
                                </span>
                                <span className="truncate text-xs text-black/55">
                                  {user.role}
                                </span>
                              </span>
                            </div>
                            <Checkbox
                              id={checkboxId}
                              checked={selectedUserIds.includes(user.id)}
                              onCheckedChange={() => toggleUser(user.id)}
                              aria-label={t("chats.selectMember", {
                                name: `${user.firstName} ${user.lastName}`,
                              })}
                            />
                          </label>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
              {errors.targetUserIds ? (
                <p className="text-custom-destructive-dark text-sm">
                  {errors.targetUserIds.message}
                </p>
              ) : null}
            </div>
          </div>

          <DialogFooter className="border-base-border shrink-0 flex-row justify-end gap-2 border-t px-6 py-4">
            <Button
              type="button"
              variant="new_outline"
              onClick={onClose}
              disabled={isCreating || isUpdating}
            >
              {t("chats.cancel")}
            </Button>
            <Button
              type="submit"
              variant="new_default"
              disabled={isCreating || isUpdating}
            >
              {getSubmitButtonLabel()}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
